import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, put, select, takeEvery, take } from 'redux-saga/effects';
import { IUpdateState, restoreState, updateState } from 'core/actions/product/state';
import {
  DialogueRegisterState,
  IActionRegister,
  IRegisterReducer,
  registerAction,
  registerActionEntity,
  registerActionState,
  registerAttempt,
  registerDialogue,
  RegisterState,
  resetAction,
} from 'core/slices/register';
import { EntityTypes } from 'constants/entities';
import { RootState } from 'types';
import { selectToken } from 'core/selectors/product/token';
import { IToken, Mode } from 'core/slices/session';
import { selectActionRegister } from 'core/selectors/product/action/actionRegister';
import { selectAction } from 'core/selectors/product/action/action';
import { IActionBasic } from 'types/domain/action';
import { selectModuleAssign } from 'core/selectors/product/module';
import { IModuleAssign } from 'types/domain/module';
import { selectActionEditor } from 'app/components/ActionEdit/slice/selectActionEditor';
import { selectIgnoreActions } from 'core/selectors/admin/ignoreActions';
import { selectActionCanUpdate } from 'core/selectors/product/action/actionCanUpdate';
import { IActionFeed, IActionFeedItem, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { selectActionEntitySelected } from 'core/selectors/product/action/actionEntitySelected';
import { loadModule } from '../module/load';
import { dialogueRequest, moduleRequest } from 'core/services/product';
import { findLastIndex } from 'utils/findLastIndex';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import { CourseNodeType, ICourseNodeDAO } from 'types/domain/course';

function* updateStateSaga(action: PayloadAction<IUpdateState>) {
  if (!action.payload.id) return;

  const ignore = yield select(selectIgnoreActions);

  if (ignore) return;

  switch (action.payload.type) {
    case EntityTypes.ACTION:
      yield registerActionSaga(action.payload.id);
      break;
    case EntityTypes.MODULE:
      yield registerModuleAttempt(action.payload.id);
      break;
    case EntityTypes.COURSE_NODE:
      yield registerCourseNode(action.payload.id);
      break;
    case EntityTypes.ACTION_ENTITY:
      const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);
      const sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
      const selected = yield select(state => selectActionEntitySelected(state, actionId, action.payload.id));
      const canUpdate = yield select(selectActionCanUpdate);
      const actionEditor = yield select(selectActionEditor);
      const disabled = selected && !canUpdate;

      if (!disabled && !actionEditor?.enabled)
        yield put(yield call(registerActionEntity, { sessionAttemptId, actionId, actionEntityId: action.payload.id }));
      break;
  }
}

function* restoreStateSaga(action: PayloadAction<IUpdateState[]>) {
  const update = action.payload.find(update => update.type === EntityTypes.ACTION);
  if (!update || !update.id) return;

  const sessionAttempt = action.payload.find(update => update.type === EntityTypes.SESSION_ATTEMPT);
  const courseProgress = action.payload.find(update => update.type === EntityTypes.COURSE_PROGRESS);
  const attemptRegister = yield select((state: RootState) => state.register.attempts[sessionAttempt.id]);
  const token = yield select((state: RootState) => state.state[EntityTypes.TOKEN]);

  if (sessionAttempt && courseProgress && !attemptRegister)
    yield put(
      yield call(registerAttempt, { sessionAttemptId: sessionAttempt.id, courseProgressId: courseProgress.id, token }),
    );

  const actionRegister: IActionRegister = yield select(state => selectActionRegister(state, update.id));
  //put actionRegister check back in for restored, otherwise getting added twice
  if (!actionRegister) yield registerActionSaga(update.id);
  else
    yield put(
      yield call(registerActionState, {
        actionId: update.id,
        sessionAttemptId: actionRegister.sessionAttemptId,
        state: RegisterState.NEW,
      }),
    );
}

export function* registerModuleAttempt(moduleId: number) {
  const token: IToken = yield select(selectToken);
  let module: IModuleAssign = yield select((state: RootState) => selectModuleAssign(state, moduleId));
  let sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
  const register: IRegisterReducer = yield select((state: RootState) => state.register);
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);

  if (!module) {
    yield loadModule(moduleId);
    yield take(moduleRequest.fulfilled);
    module = yield select((state: RootState) => selectModuleAssign(state, moduleId));
  }

  if (module.sessionAttemptId && module.sessionAttemptId !== sessionAttemptId) {
    yield put(yield call(updateState, { type: EntityTypes.SESSION_ATTEMPT, id: module.sessionAttemptId }));
    sessionAttemptId = module.sessionAttemptId;
  }

  if (module.sessionAttemptId && !register.attempts[sessionAttemptId])
    yield put(
      yield call(registerAttempt, {
        sessionAttemptId,
        courseProgressId: token ? token.courseProgressId : courseProgressId,
        moduleId: moduleId,
        token: token ? token.token : null,
        type: module.type,
      }),
    );
}

export function* registerCourseNode(courseNodeId) {
  if (courseNodeId) {
    const courseNode: ICourseNodeDAO = yield select((state: RootState) => selectCourseNode(state, courseNodeId));

    if (
      courseNode &&
      [CourseNodeType.NOTIFICATION, CourseNodeType.RESPONSE, CourseNodeType.REQUEST, CourseNodeType.MENU].includes(
        courseNode.type,
      )
    ) {
      const mode: Mode = yield select(selectSessionMode);
      const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);
      const token = yield select((state: RootState) => state.state[EntityTypes.TOKEN]);

      if (mode === Mode.LEARNING && courseNode.type === CourseNodeType.RESPONSE)
        yield put(
          yield call(dialogueRequest, {
            token,
            courseNodeId: courseNode.id,
            courseProgressId,
            state: DialogueRegisterState.NEW,
            autoNext: false,
          }),
        );
      else
        yield put(
          yield call(registerDialogue, {
            token,
            courseNodeId: courseNode.id,
            courseProgressId,
            state: DialogueRegisterState.NEW,
            created: new Date().getTime(),
            code: null,
            type: courseNode.type,
          }),
        );
    }
  }
}

export function* registerActionSaga(id: number) {
  const register: IRegisterReducer = yield select((state: RootState) => state.register);
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);

  const actionDAO: IActionBasic = yield select((state: RootState) => selectAction(state, id));
  const actionFeed: IActionFeed = yield select(selectActionFeed);

  const mode: Mode = yield select(selectSessionMode);

  if (!actionDAO) return;

  yield registerModuleAttempt(actionDAO.moduleId);

  let sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
  const isRegistered = register.attempts[sessionAttemptId] ? register.attempts[sessionAttemptId].actions[id] : null;

  if (!isRegistered)
    yield put(
      yield call(registerAction, {
        actionId: id,
        moduleId: actionDAO.moduleId,
        sessionAttemptId,
        courseProgressId,
        state: RegisterState.NEW,
      }),
    );
  else if (actionFeed.feed) {
    const index = findLastIndex(
      actionFeed.feed,
      //removed this because was causing issues with back and getting -1
      (item: IActionFeedItem) => item.actionId === id, // && item.sessionAttemptId === sessionAttemptId,
    );
    yield put(
      yield call(resetAction, {
        index: index,
        actionId: actionDAO.id,
        courseProgressId,
        sessionAttemptId,
        resetEntities: actionDAO.allowRetake,
      }),
    );
  }
}

function* watchQueue() {
  //yield takeEvery(queue, queueSaga);
}

function* watchUpdateState() {
  yield takeEvery(updateState, updateStateSaga);
}

function* watchRestoreState() {
  yield takeEvery(restoreState, restoreStateSaga);
}

export function* registerSaga() {
  yield all([fork(watchUpdateState), fork(watchRestoreState), fork(watchQueue)]);
}
